import axios from 'axios';
import { Check } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { route } from 'ziggy-js';
import Button from '@/Components/Marketing/Button';
import { FOCUS_RING } from '@/Components/Marketing/focusRing';
import Honeypot, { honeypotPayload } from '@/Components/Marketing/Honeypot';
import { useTone } from '@/Components/Marketing/Tone';
import { cn } from '@/lib/utils';

// The field takes the Subscribe button's height and radius, so the two read
// as one control; its edge is at least 3:1 on the surfaces it sits on (audit
// 15). Its text is 16px on phones, so iOS does not zoom the page on focus
// (audit 43).
const HEIGHT = { md: 'h-10 text-base sm:text-sm', lg: 'h-11 text-base sm:text-[15px]' };

// The form on each tone. On the light surfaces a white field with an ink
// edge. On the night band (Resources, 2026-10-04) the raised night-2 with a
// white edge at 35%: 3.6:1 against the night and 3.35:1 against the night
// grid's lighter cells (#161616), where white at 15% would read 1.7:1; its
// text white (17.4:1 on night-2), its placeholder night-body (6.1:1), the
// invalid edge and the error line night-orange, the success line night-body
// (9.7:1 and 6.35:1 at the least, on the grid's lighter cells).
const TONES = {
    light: {
        label: 'text-mk-ink',
        field: 'border-mk-ink/60 bg-mk-surface text-mk-ink shadow-[0_1px_2px_theme(colors.mk.ink/6%)] placeholder:text-mk-subtle focus:border-mk-ink/60',
        invalid: 'border-mk-orange-ink focus:border-mk-orange-ink',
        success: 'text-mk-body',
        error: 'text-mk-orange',
    },
    dark: {
        label: 'text-mk-night-text',
        field: 'border-white/35 bg-mk-night-2 text-mk-night-text placeholder:text-mk-night-body focus:border-white/35',
        invalid: 'border-mk-night-orange focus:border-mk-night-orange',
        success: 'text-mk-night-body',
        error: 'text-mk-night-orange',
    },
};

/**
 * The newsletter sign-up form, shared by the footer and the Resources page's
 * #newsletter section: a labelled email field and "Subscribe". It POSTs
 * exactly `{ email }` to `contact.submit.email` with axios (plus the
 * honeypot, only when a bot filled it), so the address never lands in a URL,
 * and reports the outcome inline: a 422 as an invalid
 * address, any other failure as "try again", success by clearing the field.
 * The browser's own email check runs first (quietly, via checkValidity), so
 * an obviously invalid address shows the same message without a request.
 *
 * `initialEmail` prefills the field; `onSend` is called once a valid address
 * is about to be sent (the Resources page's analytics; the footer passes
 * none); `inputRef` reaches the field (to focus it). It follows the
 * surrounding tone (TONES), as its Subscribe button does.
 */
export default function NewsletterForm({ size = 'md', initialEmail = '', onSend, inputRef: outerRef, className }) {
    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });
    const tone = TONES[useTone()];
    const [email, setEmail] = useState(initialEmail);
    const [status, setStatus] = useState('idle');
    const inputRef = useRef(null);
    const trapRef = useRef(null);
    const inputId = useId();
    const messageId = useId();

    // A later visit with another address (an Inertia visit to the same page) refills the field.
    useEffect(() => {
        if (initialEmail) setEmail(initialEmail);
    }, [initialEmail]);

    const setInput = (element) => {
        inputRef.current = element;
        if (typeof outerRef === 'function') outerRef(element);
        else if (outerRef) outerRef.current = element;
    };

    const invalid = status === 'invalid';
    const sending = status === 'sending';

    const submit = async (event) => {
        event.preventDefault();
        if (sending) return;

        if (!inputRef.current?.checkValidity()) {
            setStatus('invalid');
            inputRef.current?.focus();
            return;
        }

        setStatus('sending');
        onSend?.();

        try {
            await axios.post(route('contact.submit.email'), { email, ...honeypotPayload(trapRef.current?.value) });
            setEmail('');
            setStatus('success');
        } catch (error) {
            setStatus(error?.response?.status === 422 ? 'invalid' : 'failed');
        }
    };

    const message = {
        success: t('site.footer.newsletter.success'),
        invalid: t('site.footer.newsletter.invalid'),
        failed: t('site.footer.newsletter.failed'),
    }[status];

    return (
        // The axios submit above is the one that runs; `method` and `action`
        // send a submit it never takes to the same POST route, so the address
        // stays out of the URL (13).
        <form method="post" action={route('contact.submit.email')} onSubmit={submit} noValidate className={cn('relative', className)}>
            <Honeypot ref={trapRef} />
            <label htmlFor={inputId} className={cn('block text-sm font-medium', tone.label)}>
                {t('site.footer.newsletter.label')}
            </label>
            <div className="mt-2 flex flex-col gap-3 sm:flex-row">
                <input
                    ref={setInput}
                    id={inputId}
                    type="email"
                    name="email"
                    required
                    maxLength={100}
                    autoComplete="email"
                    value={email}
                    onChange={(event) => {
                        setEmail(event.target.value);
                        if (status !== 'sending') setStatus('idle');
                    }}
                    placeholder={t('site.footer.newsletter.placeholder')}
                    aria-invalid={invalid || undefined}
                    aria-describedby={message ? messageId : undefined}
                    className={cn(
                        // A border, not a ring: @tailwindcss/forms gives email inputs a
                        // grey border (blue on focus) that would otherwise show through.
                        'w-full min-w-0 flex-1 rounded-lg border px-3.5 transition-shadow duration-300 ease-mk',
                        // The surface's ring (focusRing.js): two-tone on the sunken footer, orange alone on the night band.
                        'focus:outline-none',
                        FOCUS_RING,
                        HEIGHT[size] ?? HEIGHT.md,
                        tone.field,
                        invalid && tone.invalid,
                    )}
                />
                <Button type="submit" size={size} disabled={sending} aria-busy={sending || undefined}>
                    {t('site.footer.newsletter.submit')}
                </Button>
            </div>
            <p
                id={messageId}
                aria-live="polite"
                className={cn(
                    'mt-3 flex min-h-5 items-center gap-1.5 text-sm',
                    status === 'success' ? tone.success : tone.error,
                )}
            >
                {status === 'success' && (
                    <Check aria-hidden="true" strokeWidth={1.5} className="size-4 shrink-0 text-mk-orange" />
                )}
                {message}
            </p>
        </form>
    );
}
